/**
 * What waits for a yes ([36](../../../docs/36-the-morning-list.md), ADR 0064).
 *
 * Every tool already keeps the things that wait for a person — a letter drafted, a post drafted, an article written,
 * a recommendation made — and each says so on its own tab. The morning list is the one place they are read together,
 * so an owner can go through the day's work in one sitting and say yes or no to each, and the morning email is the
 * same list counted. **Nothing here holds a state of its own**: a row is waiting because its own table says so, and
 * a yes or a no is the tool's own route, with the tool's own checks, its own guard and its own price.
 *
 * The API answers in codes and rows; this module is the one place they become sentences, so every kind is held to
 * its words by `waiting.test.ts` without a browser, as the activity indicator's are (27).
 */
import type { ActivityTab } from "./activity.js";
import { PLATFORMS, POST_MIN_LEAD_MS, type ChannelPlatform } from "./channels.js";
import { lettersGo } from "./coldLetterLaw.js";
import type { ProposalKind } from "./customers.js";
import type { SendingGate } from "./mailbox.js";
import type { Preparing } from "./prepare.js";
import type { ReviewAction } from "./review.js";

/**
 * Everything that can wait for a person, in the order the list draws them: the week's direction first, then a person
 * who answered, then what is about to go out under the business's name, then what is made and not out, then what is
 * only being considered.
 */
export const WAITING_KINDS = [
  "decision", "reply", "letter", "post", "made", "publish", "article", "topic", "proposal", "todo",
] as const;
export type WaitingKind = (typeof WAITING_KINDS)[number];

interface Waits {
  /** The row's own id — the one its own route takes. With `kind`, the item's key. */
  id: string;
  /** Since when it has waited. */
  at: string;
}

/** The marketing review's recommendation, which nobody has said yes or no to (35, ADR 0063). */
export interface WaitingDecision extends Waits { kind: "decision"; reviewId: string; action: ReviewAction }
/** A yes the owner gave whose work is theirs to do by hand: the fix made, the thing done elsewhere. */
export interface WaitingTodo extends Waits { kind: "todo"; reviewId: string; title: string }
/** A company answered a letter. Read in the owner's own inbox, and said how it went on Customers. */
export interface WaitingReply extends Waits { kind: "reply"; prospectId: string; company: string }
/** A first letter to a company, drafted, waiting to be sent (23, ADR 0036). */
export interface WaitingLetter extends Waits {
  kind: "letter"; prospectId: string; company: string; to: string; subject: string; opener: string | null;
  /** Written by the Customers loop rather than by a person pressing Write. */
  byLoop: boolean;
  /** The company's country, whose law may hold the letter whatever the switches say (ADR 0065). */
  country: string;
}
/** A post drafted for one network, waiting for an owner's yes (31, ADR 0056). */
export interface WaitingPost extends Waits {
  kind: "post"; platform: ChannelPlatform; caption: string; scheduledAt: string; media: "image" | "video" | null;
  /** Its network is not connected, so once approved it is the owner's to put up by hand. */
  byHand: boolean;
  /**
   * What Joogoos drew it from, when Joogoos drafted it: a section of the business page or one of its sources, by the
   * title the owner knows it by (31 §the drafter). Null for a post a person wrote.
   */
  source: string | null;
}
/** A post or video finished in the Studio that has not been posted or scheduled anywhere. */
export interface WaitingMade extends Waits { kind: "made"; projectId: string; title: string; media: "post" | "video" }
/** An article written and waiting to be read and approved — never approved from a list (ADR 0033 §3). */
export interface WaitingArticle extends Waits { kind: "article"; articleId: string; title: string; lang: string; flags: number }
/** An article approved and not yet published on the business's own site. */
export interface WaitingPublish extends Waits { kind: "publish"; articleId: string; title: string; languages: number }
/** A topic proposed for an article, waiting to be kept or dropped. Keeping one writes and spends nothing. */
export interface WaitingTopic extends Waits { kind: "topic"; articleId: string; phrase: string; angle: string }
/** A change the Customers loop proposes to its own groups (ADR 0039). */
export interface WaitingProposal extends Waits {
  kind: "proposal"; proposalId: number; proposal: ProposalKind; groupName: string | null; name: string | null;
  words: string | null; country: string | null; why: string;
}

export type WaitingItem =
  | WaitingDecision | WaitingTodo | WaitingReply | WaitingLetter | WaitingPost | WaitingMade
  | WaitingArticle | WaitingPublish | WaitingTopic | WaitingProposal;

/** `GET /v1/waiting` */
export interface WaitingResponse {
  /** In `WAITING_KINDS` order; inside a kind, the order its own tab uses (a letter best fit first, the rest oldest). */
  items: WaitingItem[];
  /** How many of each kind are waiting, counting any past what `items` carries. */
  counts: Partial<Record<WaitingKind, number>>;
  /** Whether the person reading may give the owner's yes: send, approve a post, decide the review or a proposal. */
  mayDecide: boolean;
  /** What a yes to a letter does on this box today, so the list can say it before the press rather than after. */
  letters: { gate: SendingGate; sendingOn: boolean };
  /** The morning email's switch, for this business. */
  morningEmail: boolean;
  /** What Joogoos may spend a week preparing this list overnight, and what it has (ADR 0064 point 5). */
  preparing: Preparing;
  asOf: string;
}

/**
 * `PATCH /v1/waiting/settings` — the owner's alone. Either or both: the morning email's switch, and the week's amount
 * Joogoos may spend preparing, in dollars as the owner types them (`PrepareWeekly`).
 */
export interface WaitingSettings { morningEmail?: boolean; prepareWeekly?: string; reviewWeekly?: boolean }

/** What the settings are once changed: both, whichever was sent. */
export interface WaitingSettingsResponse { morningEmail: boolean; preparing: Preparing }

/** At most this many of one kind on the list: the rest are on the kind's own tab, and `counts` says how many. */
export const WAITING_PER_KIND = 10;

/**
 * A Studio post made this long ago and never put anywhere has been left, or put up by hand — it stops waiting. Three
 * days and not fourteen: shipped with fourteen, the dev box's list read "25 finished posts", every video made and
 * downloaded in a fortnight, each one asking again every morning with no way to say it went up by hand.
 */
export const MADE_WAITS_DAYS = 3;

/** The kinds only an owner may say yes or no to, because each sends, publishes or decides under the business's name. */
const OWNER_ONLY: ReadonlySet<WaitingKind> = new Set(["decision", "todo", "letter", "post", "proposal"]);

/** How an item is said, and what its buttons say. `yes` and `no` are null where the list offers no such press. */
export interface WaitingSaid {
  title: string;
  detail: string;
  /** One line that changes what a yes means here, said before the press: a rehearsal, a switch that is off. */
  note: string | null;
  yes: string | null;
  no: string | null;
  /** The door to the thing on its own tab, where it can be read whole and changed. */
  open: string;
  tab: ActivityTab;
  ownerOnly: boolean;
}

export interface WaitingContext {
  gate: SendingGate;
  sendingOn: boolean;
  now: Date;
}

const clip = (s: string, n = 140) => {
  const t = s.replace(/\s+/g, " ").trim();
  return t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t;
};

/** What a proposal would actually do, in the words of the thing it changes rather than the name of its kind. */
export function proposalWhat(p: { kind: ProposalKind; groupName: string | null; words: string | null; country: string | null }): string {
  if (p.kind === "reword") return `Look for “${p.words}” instead.`;
  if (p.kind === "narrow") return `Make a group inside ${p.groupName} looking for “${p.words}”.`;
  if (p.kind === "try_market") return `Try it in ${p.country} as well.`;
  return "Stop looking for this group. Nothing already found is deleted.";
}

/**
 * When an approved post goes out: at the time it was drafted for while that is still ahead, and otherwise as soon as
 * the publishing route allows. The route refuses a time inside `POST_MIN_LEAD_MS`, so the soonest is just past it.
 */
export function postGoesOutAt(item: Pick<WaitingPost, "scheduledAt">, now: Date): Date {
  const planned = new Date(item.scheduledAt);
  const soonest = new Date(now.getTime() + 2 * POST_MIN_LEAD_MS);
  return planned.getTime() >= soonest.getTime() ? planned : soonest;
}

/** What a yes to a letter does on this box and for this business, when it is not simply "it goes". */
function letterNote(ctx: WaitingContext): string | null {
  if (ctx.gate === "off") return "Letters cannot leave this server yet, so a yes keeps it waiting.";
  // A rehearsed letter is marked sent like a real one, and so is its company (`markSent`): it reached nobody, and the
  // company is never written to again. Said before the press, because the press cannot be taken back.
  if (ctx.gate === "rehearsal") return "Rehearsing: it is delivered to your own address, not to them — and the company then counts as written to, so it is never written to again.";
  if (ctx.gate === "allowlist") return "Only our own addresses can be written to from this server, so this one is refused.";
  if (!ctx.sendingOn) return "Sending is off for this business: a yes keeps it waiting until you turn it on.";
  return null;
}

/** Every item, in words. The one place a code becomes a sentence; nothing here says job, queue or workspace. */
export function waitingSaid(item: WaitingItem, ctx: WaitingContext): WaitingSaid {
  const ownerOnly = OWNER_ONLY.has(item.kind);
  switch (item.kind) {
    case "decision":
      return {
        title: `Your marketing review recommends: ${item.action.title}`,
        detail: clip(item.action.why, 220),
        note: item.action.experiment ? "A small test, because the week was too thin to learn a pattern from." : null,
        yes: "Approve", no: "Not this", open: "Read the review", tab: "Overview", ownerOnly,
      };
    case "todo":
      return {
        title: item.title,
        detail: "You said yes to this, and it is yours to do. Mark it done once it is.",
        note: null, yes: "Mark it done", no: null, open: "Read the review", tab: "Overview", ownerOnly,
      };
    case "reply":
      return {
        title: `${item.company} answered your letter`,
        detail: "Read it in your own inbox, then say how it went on Customers.",
        note: null, yes: null, no: null, open: "Open on Customers", tab: "Customers", ownerOnly,
      };
    case "letter": {
      // A draft written before its company's country was held: no yes is offered, because none could send it, and
      // the reason is said where the yes would be (ADR 0065). It waits only to be thrown away.
      const law = lettersGo(item.country);
      return {
        title: `A letter to ${item.company}`,
        detail: clip(`To ${item.to} · “${item.subject}”${item.opener ? ` — ${item.opener}` : ""}`, 220),
        note: law.goes ? letterNote(ctx) : law.says,
        yes: !law.goes ? null : ctx.gate === "rehearsal" ? "Rehearse" : "Send", no: "Throw it away", open: "Read it whole",
        tab: "Customers", ownerOnly,
      };
    }
    case "post": {
      const label = PLATFORMS[item.platform].label;
      const out = postGoesOutAt(item, ctx.now);
      const soon = out.getTime() - ctx.now.getTime() <= 3 * POST_MIN_LEAD_MS;
      return {
        title: `A ${item.media === "video" ? "video" : "post"} for ${label}`,
        // What it was drawn from, beside it, so a yes is two things read side by side rather than the website
        // remembered (31 §the drafter).
        detail: `${clip(item.caption || "No words with it.")}${item.source ? ` · From ${item.source}` : ""}`,
        note: item.byHand
          ? `${label} is not connected, so once approved it is yours to put up by hand.`
          : soon ? `Approved, it goes out in about two minutes.` : `Approved, it goes out at the time it was planned for.`,
        yes: "Approve", no: "Not this", open: "Open it", tab: "Calendar", ownerOnly,
      };
    }
    case "made":
      return {
        title: `“${item.title}” is made and has not gone out`,
        detail: `Post it now or later, or download the ${item.media === "video" ? "video" : "picture"} and put it up yourself.`,
        note: null, yes: null, no: null, open: "Post it", tab: "Studio", ownerOnly,
      };
    case "publish":
      return {
        title: `“${item.title}” is approved and not on your website yet`,
        detail: item.languages > 1
          ? `Publishing puts it on your website in ${item.languages} languages at once.`
          : "Publishing puts it on your website.",
        note: null, yes: "Publish", no: null, open: "Open on SEO", tab: "SEO", ownerOnly,
      };
    case "article":
      return {
        title: `An article to read: “${item.title}”`,
        detail: item.flags
          ? `${item.flags} thing${item.flags === 1 ? "" : "s"} to check before it can be approved. Nothing is published until you do.`
          : "Read it before you approve it. Nothing is published until you do.",
        note: null, yes: null, no: null, open: "Read it", tab: "SEO", ownerOnly,
      };
    case "topic":
      return {
        title: `A topic to write about: “${item.phrase}”`,
        detail: clip(item.angle || "Keep it to have it written; keeping it writes and spends nothing yet."),
        note: null, yes: "Keep it", no: "Drop it", open: "Open on SEO", tab: "SEO", ownerOnly,
      };
    case "proposal":
      return {
        title: `Customers proposes: ${proposalWhat({ ...item, kind: item.proposal })}`,
        detail: clip(item.why, 220),
        note: null, yes: "Apply", no: "Not this", open: "Open on Customers", tab: "Customers", ownerOnly,
      };
  }
}

const NOUNS: Record<WaitingKind, [string, string]> = {
  decision: ["recommendation", "recommendations"],
  todo: ["thing you said you would do", "things you said you would do"],
  reply: ["reply", "replies"],
  letter: ["letter", "letters"],
  post: ["post", "posts"],
  made: ["finished post", "finished posts"],
  publish: ["article to publish", "articles to publish"],
  article: ["article to read", "articles to read"],
  topic: ["topic", "topics"],
  proposal: ["change to Customers", "changes to Customers"],
};

/** "3 letters, 1 post and 2 topics" — the counts as a person would say them, in the list's own order. */
export function waitingCounted(counts: Partial<Record<WaitingKind, number>>): string {
  const parts = WAITING_KINDS.flatMap((k) => {
    const n = counts[k] ?? 0;
    return n ? [`${n} ${NOUNS[k][n === 1 ? 0 : 1]}`] : [];
  });
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/** How many things wait, all kinds together. */
export const waitingTotal = (counts: Partial<Record<WaitingKind, number>>): number =>
  WAITING_KINDS.reduce((n, k) => n + (counts[k] ?? 0), 0);
